import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Wrench, PaintRoller, Home as HomeIcon, Grid3x3, Boxes, Sparkles, Scissors,
  Brush, Hammer, Trees, Zap, Wind, Droplets, Truck, Wifi, Bug, Car, Camera, Tag,
  Search, ArrowRight, ShieldCheck, Star, MapPin, LocateFixed,
} from "lucide-react";

const ICONS = {
  Wrench, PaintRoller, Home: HomeIcon, Grid3x3, Boxes, Sparkles, Scissors, Brush,
  Hammer, Trees, Zap, Wind, Droplets, Truck, Wifi, Bug, Car, Camera, Tag,
};

const HERO = "https://images.unsplash.com/photo-1646227655685-a530813759b3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwzfHxjb25zdHJ1Y3Rpb24lMjB3b3JrZXIlMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODUwNjgzOTF8MA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const { t, catLabel, bizName, bizDesc } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();

  const goStart = () => {
    if (!user) return navigate("/register");
    if (user.role === "business") return navigate("/dashboard");
    if (user.role === "admin") return navigate("/admin");
    return navigate("/onboarding"); // client / pending -> pick business & create it
  };
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [featuredCity, setFeaturedCity] = useState(null);
  const [city, setCity] = useState("");
  const [cat, setCat] = useState("");

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/geo/featured").then((r) => { setFeatured(r.data.businesses || []); setFeaturedCity(r.data.city || null); }).catch(() => {});
  }, []);

  const doSearch = () => {
    const params = new URLSearchParams();
    if (city) params.set("city", city);
    if (cat) params.set("category", cat);
    navigate(`/directory?${params.toString()}`);
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Tu navegador no soporta geolocalización"); return; }
    toast.info("Obteniendo tu ubicación…");
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          const geo = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=es`).then((r) => r.json());
          const c = geo.city || geo.locality;
          if (!c) { toast.error("No pudimos determinar tu ciudad"); return; }
          const { data } = await api.get("/geo/featured", { params: { city: c } });
          setFeatured(data.businesses || []);
          setFeaturedCity(data.city);
          if (data.city) toast.success(`Mostrando negocios en ${data.city}`);
          else toast.info(`No hay negocios en ${c} todavía. Mostrando destacados.`);
        } catch { toast.error("No pudimos obtener tu ubicación"); }
      },
      () => toast.error("Permiso de ubicación denegado"),
      { timeout: 8000 }
    );
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-secondary">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="h-full w-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-secondary via-secondary/80 to-secondary/30" />
        </div>
        <div className="relative mx-auto max-w-7xl px-6 py-24 md:py-32">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-2xl">
            <span className="inline-block rounded-sm border border-primary/40 bg-primary/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] text-primary" data-testid="hero-tag">
              {t("hero_tag")}
            </span>
            <h1 className="mt-6 font-head text-4xl font-extrabold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="hero-title">
              {t("hero_title")}
            </h1>
            <p className="mt-5 max-w-xl text-base text-slate-300 md:text-lg">{t("hero_sub")}</p>

            <div className="mt-8 flex flex-col gap-3 rounded-sm border border-slate-700 bg-white/95 p-3 shadow-2xl backdrop-blur sm:flex-row" data-testid="hero-search-box">
              <div className="flex flex-1 items-center gap-2 border-b border-slate-200 px-2 pb-2 sm:border-b-0 sm:border-r sm:pb-0">
                <MapPin className="h-5 w-5 text-slate-400" />
                <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder={t("search_city")} className="border-0 shadow-none focus-visible:ring-0" data-testid="hero-city-input" />
              </div>
              <select value={cat} onChange={(e) => setCat(e.target.value)} className="flex-1 bg-transparent px-2 text-sm text-slate-700 outline-none" data-testid="hero-category-select">
                <option value="">{t("all_categories")}</option>
                {categories.map((c) => (<option key={c.key} value={c.key}>{catLabel(c)}</option>))}
              </select>
              <Button onClick={doSearch} className="rounded-sm bg-primary hover:bg-orange-600" data-testid="hero-search-btn">
                <Search className="mr-2 h-4 w-4" />{t("search_btn")}
              </Button>
            </div>

            <div className="mt-6 flex items-center gap-4 text-sm text-slate-400">
              <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-accent" /> Profesionales verificados</span>
              <span className="flex items-center gap-1.5"><Star className="h-4 w-4 text-primary" /> +1,000 negocios</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Categories */}
      <section className="mx-auto max-w-7xl px-6 py-16">
        <h2 className="font-head text-2xl font-extrabold tracking-tight text-secondary md:text-3xl">{t("popular_categories")}</h2>
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {categories.map((c, i) => {
            const Icon = ICONS[c.icon] || Wrench;
            return (
              <motion.button
                key={c.key}
                initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.03 }}
                onClick={() => navigate(`/directory?category=${c.key}`)}
                className="group flex flex-col items-center gap-3 rounded-sm border border-slate-200 bg-white p-6 text-center shadow-sm transition-transform hover:-translate-y-1 hover:border-primary hover:shadow-lg"
                data-testid={`category-card-${c.key}`}
              >
                <span
                  className={`flex h-12 w-12 items-center justify-center rounded-sm transition-colors ${c.color ? "text-white" : "bg-slate-100 text-secondary group-hover:bg-primary group-hover:text-white"}`}
                  style={c.color ? { backgroundColor: c.color } : undefined}
                >
                  <Icon className="h-6 w-6" />
                </span>
                <span className="text-sm font-semibold text-slate-700">{catLabel(c)}</span>
              </motion.button>
            );
          })}
        </div>
      </section>

      {/* Featured */}
      {featured.length > 0 && (
        <section className="bg-slate-100/60 py-16">
          <div className="mx-auto max-w-7xl px-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-head text-2xl font-extrabold tracking-tight text-secondary md:text-3xl" data-testid="featured-heading">
                  {featuredCity ? `${t("featured")} · ${featuredCity}` : t("featured")}
                </h2>
                {featuredCity && <p className="mt-1 text-sm text-slate-500" data-testid="featured-geo-note">{t("featured_geo_note")}</p>}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="rounded-sm" onClick={useMyLocation} data-testid="use-location-btn"><LocateFixed className="mr-1 h-4 w-4" />{t("use_location")}</Button>
                <Button variant="ghost" className="rounded-sm" onClick={() => navigate(featuredCity ? `/directory?city=${encodeURIComponent(featuredCity)}` : "/directory")} data-testid="view-all-btn">
                  {t("nav_directory")} <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {featured.map((b) => (
                <div key={b.id} onClick={() => navigate(`/business/${b.id}`)} className="cursor-pointer overflow-hidden rounded-sm border border-slate-200 bg-white shadow-sm transition-transform hover:-translate-y-1 hover:shadow-lg" data-testid={`featured-card-${b.id}`}>
                  <div className="h-36 bg-slate-200">
                    {b.cover_image ? <img src={b.cover_image} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-slate-400"><HomeIcon className="h-10 w-10" /></div>}
                  </div>
                  <div className="p-5">
                    <h3 className="font-head text-lg font-bold text-secondary">{bizName(b)}</h3>
                    <p className="mt-1 flex items-center gap-1 text-sm text-slate-500"><MapPin className="h-3.5 w-3.5" /> {b.city}, {b.state}</p>
                    <p className="mt-2 line-clamp-2 text-sm text-slate-600">{bizDesc(b)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* How it works */}
      <section className="mx-auto max-w-7xl px-6 py-16">
        <h2 className="font-head text-2xl font-extrabold tracking-tight text-secondary md:text-3xl">{t("how_title")}</h2>
        <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
          {[["how_1_t", "how_1_d"], ["how_2_t", "how_2_d"], ["how_3_t", "how_3_d"]].map(([tk, dk], i) => (
            <div key={tk} className="rounded-sm border border-slate-200 bg-white p-8">
              <span className="font-head text-4xl font-extrabold text-primary">0{i + 1}</span>
              <h3 className="mt-3 font-head text-xl font-bold text-secondary">{t(tk)}</h3>
              <p className="mt-2 text-sm text-slate-600">{t(dk)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Grow CTA */}
      <section className="bg-secondary">
        <div className="mx-auto max-w-7xl px-6 py-16 text-center md:py-20">
          <h2 className="font-head text-3xl font-extrabold tracking-tight text-white md:text-4xl">{t("grow_title")}</h2>
          <p className="mx-auto mt-4 max-w-2xl text-slate-300">{t("grow_sub")}</p>
          <Button onClick={() => navigate("/register")} size="lg" className="mt-8 rounded-sm bg-primary px-8 hover:bg-orange-600" data-testid="grow-cta-btn">
            {t("grow_cta")} <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>
    </div>
  );
}
